import { initKeycloak, login, logout, getToken, getUsername, hasRole } from "./auth";
import { setTokenGetter, api } from "./api";
import { Icons } from "./utils/icons";
import { validatePassword, mountPasswordChecklist } from "./utils/validation";
import { setPortalFeatures, hasPortalFeature } from "./utils/portal-features";
import { setWriteAccess } from "./utils/permissions";
import { initModal, openModal, closeModal, modalBody } from "./components/modal";
import * as dashboard from "./pages/dashboard";
import * as classesPage from "./pages/classes";
import * as studentsPage from "./pages/students";
import * as practicesPage from "./pages/practices";
import * as mapPage from "./pages/map";
import * as planningPage from "./pages/planning";
import * as stundenplanPage from "./pages/stundenplan";
import * as auswertungenPage from "./pages/auswertungen";
import * as adminPage from "./pages/admin";
import * as adminPracticesPage from "./pages/admin-practices";
import * as adminDepartmentsPage from "./pages/admin-departments";
import * as adminLehrplaenePage from "./pages/admin-lehrplaene";
import * as adminApiPage from "./pages/admin-api";
import * as adminTraegerPage from "./pages/admin-traeger";
import * as usersPage from "./pages/users";
import * as settingsHubPage from "./pages/settings-hub";
import * as kreisverbaendePage from "./pages/kreisverbaende";
import * as sharedDocumentsPage from "./pages/shared-documents";
import * as registerPage from "./pages/register";
import * as registerPracticePage from "./pages/register-practice";
import * as meineEinrichtungPage from "./pages/meine-einrichtung";
import * as practiceSettingsPage from "./pages/practice-settings";
import * as practicePlanningPage from "./pages/practice-planning";
import * as practiceUsersPage from "./pages/practice-users";
import * as practiceMyDocumentsPage from "./pages/practice-documents";
import * as studentHomePage from "./pages/student-home";
import * as studentPlanningPage from "./pages/student-planning";
import * as studentTimetablePage from "./pages/student-timetable";
import * as studentAbsencePage from "./pages/student-abwesenheit";
import * as studentDepartmentsPage from "./pages/student-departments";
import * as studentPracticesPage from "./pages/student-practices";
import * as studentMyDocumentsPage from "./pages/student-my-documents";
import * as studentSteckbriefPage from "./pages/student-steckbrief";
import * as billingPage from "./pages/billing";
import * as agbPage from "./pages/agb";
import * as avvPage from "./pages/avv";
import * as avvPracticePage from "./pages/avv-praktikumsstelle";
import * as traegerUebersichtPage from "./pages/traeger-uebersicht";
import * as traegerSchulenPage from "./pages/traeger-schulen";
import * as traegerBenutzerPage from "./pages/traeger-benutzer";
import * as traegerEinstellungenPage from "./pages/traeger-einstellungen";
import { getCarrierView, setCarrierView, switchCarrierView } from "./utils/carrier-view";
import type { UserInfo } from "./types";
import { esc } from "./utils/html";

// Build-Version für Diagnose
const BUILD_VERSION = "2026-09-10-A";
console.log("%c[Meduvio] app.js geladen, Version: " + BUILD_VERSION, "color:green;font-weight:bold");

// DOM-Referenzen
const $ = (id: string) => document.getElementById(id)!;

// Menuepunkte mit feature erscheinen nur, wenn die Schule das Modul gebucht hat.
// Gesperrte Module zeigt das Dashboard, nicht die Leiste.
const SCHOOL_NAV: {
  hash: string; label: string; icon: string;
  feature?: string; adminOnly?: boolean;
  /** Sichtbar, sobald EINES dieser Module freigeschaltet ist. Fuer Seiten, die auf
   *  mehreren Modulen aufsetzen und selbst kein kaufbares Modul sind. */
  anyFeature?: string[];
}[] = [
  { hash: "dashboard", label: "Dashboard", icon: Icons.dashboard },
  { hash: "klassen", label: "Klassen", icon: Icons.academicCap },
  { hash: "schueler", label: "Schüler", icon: Icons.user },
  { hash: "praktikumsstellen", label: "Praktikumsstellen", icon: Icons.buildingOffice2 },
  // Nur DRK-Schulen; ohne Freischaltung "Seite nicht gefunden" statt Modul-Hinweis (siehe navigate)
  { hash: "kreisverbaende", label: "Kreisverbände", icon: Icons.mapPin, feature: "drkKreisverband" },
  { hash: "karte", label: "Karte", icon: Icons.map },
  { hash: "planung", label: "Planung", icon: Icons.calendarDays, feature: "planung" },
  { hash: "stundenplan", label: "Stundenplan", icon: Icons.clipboardDocumentList, feature: "stundenplan" },
  { hash: "auswertungen", label: "Auswertungen", icon: Icons.chartBar, anyFeature: ["planung", "stundenplan", "schuelerPortal"] },
];

/** Fehlt jedes der geforderten Module? Ohne anyFeature nie. */
function anyFeatureMissing(item: { anyFeature?: string[] }): boolean {
  return !!item.anyFeature && !item.anyFeature.some(f => userFeatures.includes(f));
}

/** Ist der Menuepunkt mangels gebuchtem Modul gesperrt? */
function isNavLocked(item: { feature?: string; anyFeature?: string[] }): boolean {
  return (!!item.feature && !userFeatures.includes(item.feature)) || anyFeatureMissing(item);
}

/**
 * Hinweis beim Direktaufruf einer Seite, deren Modul nicht gebucht ist (URL, altes
 * Lesezeichen). Den Weg zu den Abonnements gibt es nur fuer Schul-Admins, alle anderen
 * duerften die Seite ohnehin nicht oeffnen.
 */
function renderModuleNotBooked(content: HTMLElement, item: { label: string; anyFeature?: string[] }) {
  const message = item.anyFeature
    ? `${item.label} gibt es, sobald eines der Module Planung, Stundenplan oder Schüler-Portal gebucht ist.`
    : `Das Modul „${item.label}“ ist für deine Schule nicht gebucht.`;
  const action = isSchulAdmin
    ? `<a class="btn btn-primary" href="#/abonnements">Zu den Abonnements</a>`
    : `<p style="font-size:.875rem;color:var(--gray-600);margin:0">Buchen kann es die Administration deiner Schule.</p>`;
  content.innerHTML = `
    <div class="page-header"><h1>${item.label}</h1></div>
    <div class="card" style="padding:1.5rem">
      <p style="font-size:.875rem;color:var(--gray-600);margin:0 0 1rem">${message}</p>
      ${action}
    </div>`;
}

/** Markiert den Menuepunkt der Route; gesperrte Seiten haben keinen, dann ist keiner aktiv. */
function markActiveNav(route: string) {
  // Unterpunkte der Einstellungen auf- bzw. zuklappen und den aktiven markieren. Muss vor
  // dem Markieren laufen: der Fuss wird neu aufgebaut und verloere sonst seine Markierung.
  renderSidebarFooter();
  document.querySelectorAll(".nav-link").forEach(link => {
    link.classList.toggle("active", (link as HTMLElement).dataset.route === route);
  });
}

const ADMIN_NAV = [
  { hash: "admin", label: "Mandantenverwaltung", icon: Icons.buildingLibrary },
  { hash: "admin-traeger", label: "Schulträger", icon: Icons.rectangleGroup },
  { hash: "admin-praktikumsstellen", label: "Praktikumsstellen", icon: Icons.buildingOffice2 },
  { hash: "admin-fachbereiche", label: "Fachbereiche", icon: Icons.tag },
  { hash: "admin-lehrplaene", label: "Lehrplan-Vorlagen", icon: Icons.clipboardDocumentList },
  { hash: "admin-api", label: "API-Dokumentation", icon: Icons.codeBracket },
];

// Träger-Bereich: eigene Leiste, getrennt von der Schulnavigation
const CARRIER_NAV: { hash: string; label: string; icon: string; founderOnly?: boolean }[] = [
  { hash: "traeger", label: "Dashboard", icon: Icons.dashboard },
  { hash: "traeger-schulen", label: "Schulen", icon: Icons.buildingLibrary },
  { hash: "traeger-benutzer", label: "Träger-User", icon: Icons.userGroup, founderOnly: true },
  { hash: "traeger-einstellungen", label: "Einstellungen", icon: Icons.cog6Tooth },
];

// Praktikumsstellen-Portal-Navigation (für selbst-registrierte Praktikumsstellen)
const PRACTICE_NAV: { hash: string; label: string; icon: string; adminOnly?: boolean }[] = [
  { hash: "meine-einrichtung", label: "Meine Einrichtung", icon: Icons.buildingOffice2 },
  { hash: "praktikumsplanung", label: "Praktikumsplanung", icon: Icons.calendarDays },
  { hash: "praxis-dokumente", label: "Meine Dokumente", icon: Icons.documentText },
  { hash: "praxis-benutzer", label: "Benutzer", icon: Icons.userGroup, adminOnly: true },
];

// Schüler-Portal-Navigation (für eingeloggte Schüler)
// feature = Abo-Feature der Schule, portalFeature = individuell abschaltbare Funktion
// (Einstellungen → Schüler-Portal), siehe utils/portal-features.ts
type StudentNavItem = { hash: string; label: string; icon: string; feature?: string; portalFeature?: string };
const STUDENT_NAV: StudentNavItem[] = [
  { hash: "mein-dashboard", label: "Mein Dashboard", icon: Icons.home },
  { hash: "fachbereiche", label: "Fachbereiche", icon: Icons.tag, portalFeature: "fachbereiche" },
  { hash: "meine-praktikumsstellen", label: "Praktikumsstellen", icon: Icons.buildingOffice2, portalFeature: "praktikumsstellen" },
  { hash: "meine-planung", label: "Meine Planung", icon: Icons.calendarDays, portalFeature: "meinePlanung" },
  { hash: "mein-stundenplan", label: "Mein Stundenplan", icon: Icons.clipboardDocumentList, feature: "stundenplan", portalFeature: "meinStundenplan" },
  { hash: "meine-abwesenheiten", label: "Meine Abwesenheiten", icon: Icons.calendarDays, feature: "stundenplan", portalFeature: "abwesenheitMelden" },
  { hash: "meine-dokumente", label: "Meine Dokumente", icon: Icons.documentText, portalFeature: "dokumente" },
  { hash: "mein-steckbrief", label: "Mein Steckbrief", icon: Icons.user, portalFeature: "steckbrief" },
];

// Sichtbarkeit eines Schüler-Menüpunkts. "Meine Planung" ist ein Sonderfall: ohne
// Planungs-Modul ist "meinePlanung" gesperrt, die Seite zeigt dann nur die Wunschliste.
// Ist auch die aus, bleibt nichts übrig.
function isStudentNavVisible(item: StudentNavItem): boolean {
  if (item.hash === "meine-planung") {
    return hasPortalFeature("meinePlanung") || hasPortalFeature("wunschliste");
  }
  if (item.feature && !userFeatures.includes(item.feature)) return false;
  return hasPortalFeature(item.portalFeature);
}

const PAGES: Record<string, { render: (el: HTMLElement) => Promise<void> }> = {
  dashboard, klassen: classesPage, schueler: studentsPage,
  praktikumsstellen: practicesPage, karte: mapPage, planung: planningPage,
  stundenplan: stundenplanPage, auswertungen: auswertungenPage,
  "verteilte-dokumente": sharedDocumentsPage,
  benutzer: usersPage,
  einstellungen: settingsHubPage,
  abonnements: billingPage,
  kreisverbaende: kreisverbaendePage,
  admin: adminPage, "admin-praktikumsstellen": adminPracticesPage,
  "admin-fachbereiche": adminDepartmentsPage, "admin-api": adminApiPage,
  "admin-lehrplaene": adminLehrplaenePage,
  "admin-traeger": adminTraegerPage,
  registrieren: registerPage,
  "meine-einrichtung": meineEinrichtungPage,
  "praktikumsplanung": practicePlanningPage,
  "praxis-einstellungen": practiceSettingsPage,
  "praxis-benutzer": practiceUsersPage,
  "praxis-dokumente": practiceMyDocumentsPage,
  // Schüler-Portal-Seiten
  "mein-dashboard": studentHomePage,
  "fachbereiche": studentDepartmentsPage,
  "meine-praktikumsstellen": studentPracticesPage,
  "meine-planung": studentPlanningPage,
  "mein-stundenplan": studentTimetablePage,
  "meine-abwesenheiten": studentAbsencePage,
  "meine-dokumente": studentMyDocumentsPage,
  "mein-steckbrief": studentSteckbriefPage,
  // Träger-Bereich
  traeger: traegerUebersichtPage,
  "traeger-schulen": traegerSchulenPage,
  "traeger-benutzer": traegerBenutzerPage,
  "traeger-einstellungen": traegerEinstellungenPage,
};

let isPlatformAdmin = false;
let isSchulAdmin = false;
let isSchueler = false;
let isPraktikumsstelle = false;
let isPracticeAdmin = false;
let hasSchool = false;
let userFeatures: string[] = [];
// Schulträger: carrier ist gesetzt, wenn der User Träger-User ist. inCarrierArea: er ist
// gerade im Träger-Bereich (statt in einer Schule).
let carrier: UserInfo["carrier"] = null;
let inCarrierArea = false;

// Router
function getRoute(): string {
  const hash = window.location.hash.replace("#/", "").split("?")[0];
  if (isPraktikumsstelle) {
    return hash || "meine-einrichtung";
  }
  if (isSchueler) {
    return hash || "mein-dashboard";
  }
  if (inCarrierArea) {
    return hash || "traeger";
  }
  return hash || (isPlatformAdmin && !hasSchool ? "admin" : "dashboard");
}

async function navigate() {
  // Sidebar auf Mobile schließen beim Navigieren
  const sidebarEl = document.querySelector(".sidebar") as HTMLElement | null;
  const mobileOverlay = document.getElementById("mobile-overlay");
  if (sidebarEl) sidebarEl.classList.remove("sidebar-open");
  if (mobileOverlay) mobileOverlay.classList.remove("overlay-open");

  const route = getRoute();
  const content = $("main-content");

  // Praktikumsstellen dürfen nur ihren eigenen Bereich aufrufen
  if (isPraktikumsstelle) {
    const allowedPracticeRoutes = [...PRACTICE_NAV.map(n => n.hash), "praxis-einstellungen"];
    if (!allowedPracticeRoutes.includes(route)) {
      content.innerHTML = `<div class="alert alert-error">Zugriff verweigert.</div>`;
      return;
    }
    const navItem = PRACTICE_NAV.find(n => n.hash === route);
    if (navItem?.adminOnly && !isPracticeAdmin) {
      content.innerHTML = `<div class="alert alert-error">Zugriff verweigert – nur Praktikumsstellen-Administratoren.</div>`;
      return;
    }
  } else if (isSchueler) {
    const navItem = STUDENT_NAV.find(n => n.hash === route);
    if (!navItem) {
      content.innerHTML = `<div class="alert alert-error">Zugriff verweigert.</div>`;
      return;
    }
    // Abgeschaltete Funktionen auch beim direkten Aufruf über die URL sperren
    if (!isStudentNavVisible(navItem)) {
      content.innerHTML = `<div class="alert alert-error">Funktion „${navItem.label}" ist für deine Schule nicht aktiviert.</div>`;
      return;
    }
  } else if (inCarrierArea) {
    // Im Träger-Bereich gibt es nur dessen Seiten; alles andere führt zum Dashboard
    const navItem = CARRIER_NAV.find(n => n.hash === route);
    if (!navItem) { window.location.hash = "#/traeger"; return; }
    if (navItem.founderOnly && !carrier?.isFounder) {
      content.innerHTML = `<div class="alert alert-error">Zugriff verweigert, nur für den Haupt-Trägeradmin.</div>`;
      return;
    }
  } else if (route.startsWith("traeger")) {
    // Aus einer Schule in den Träger-Bereich (z. B. Link aus den Einstellungen)
    if (carrier) { switchCarrierView({ mode: "traeger" }, `#/${route}`); return; }
    content.innerHTML = `<div class="alert alert-error">Zugriff verweigert, kein Zugang zu einem Schulträger.</div>`;
    return;
  } else {
    // Zugriffsschutz: Admin-Seiten nur für Plattform-Admins
    const isAdminRoute = route === "admin" || route === "admin-praktikumsstellen" || route === "admin-fachbereiche" || route === "admin-api" || route === "admin-traeger" || route === "admin-lehrplaene";
    if (isAdminRoute && !isPlatformAdmin) {
      content.innerHTML = `<div class="alert alert-error">Zugriff verweigert – nur Plattform-Administratoren.</div>`;
      return;
    }
    // DRK-Kreisverbände gibt es nur für freigeschaltete Schulen. Ohne Feature verhält sich
    // die Route wie jede unbekannte, damit nichts auf die Funktion hinweist. Muss vor dem
    // Modul-Check unten laufen, der sonst "nicht gebucht" anzeigen würde.
    const hasKreisverbaende = userFeatures.includes("drkKreisverband");
    if (route === "kreisverbaende" && !hasKreisverbaende) {
      markActiveNav(route);
      content.innerHTML = `<div class="empty-state"><p>Seite nicht gefunden.</p></div>`;
      return;
    }
    // Früher ein Reiter der Einstellungen: alte Lesezeichen auf den Menüpunkt umleiten
    const tab = new URLSearchParams(window.location.hash.split("?")[1] || "").get("tab");
    if (route === "einstellungen" && tab === "kreisverbaende" && hasKreisverbaende) {
      window.location.replace("#/kreisverbaende");
      return;
    }
    // Benutzer-, Einstellungen- und Abonnements-Seite nur für Schul-Admins
    if ((route === "benutzer" || route === "einstellungen" || route === "abonnements" || route === "verteilte-dokumente") && !isSchulAdmin) {
      content.innerHTML = `<div class="alert alert-error">Zugriff verweigert – nur Schul-Administratoren.</div>`;
      return;
    }
    // Seiten nicht gebuchter Module: Hinweis statt Weiterleitung. Seiten, die auf
    // mehreren Modulen aufsetzen, sperren erst, wenn keines davon da ist.
    const navItem = SCHOOL_NAV.find(n => n.hash === route);
    if (navItem && isNavLocked(navItem)) {
      markActiveNav(route);
      renderModuleNotBooked(content, navItem);
      return;
    }
    // Schul-Seiten nur für Benutzer mit Schulzuordnung
    if (!isAdminRoute && !hasSchool) {
      content.innerHTML = `<div class="empty-state"><p>Kein Schulzugang vorhanden.</p><p style="margin-top:.5rem">Du bist als Plattform-Admin angemeldet, aber keiner Schule zugeordnet.</p></div>`;
      return;
    }
  }

  markActiveNav(route);

  const page = PAGES[route];
  if (page) {
    console.log("[Meduvio] Navigiere zu:", route);
    content.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:200px"><div class="spinner"></div></div>`;
    try { await page.render(content); console.log("[Meduvio] Seite gerendert:", route); }
    catch (err: any) {
      console.error("[Meduvio] Render-Fehler:", err);
      const errDiv = document.createElement("div");
      errDiv.className = "alert alert-error";
      errDiv.textContent = `Fehler: ${err.message}`;
      content.replaceChildren(errDiv);
    }
  } else {
    content.innerHTML = `<div class="empty-state"><p>Seite nicht gefunden.</p></div>`;
  }
}

// Sidebar aufbauen
function buildSidebar() {
  const nav = $("sidebar-nav");
  let html = "";

  if (isPraktikumsstelle) {
    html += PRACTICE_NAV
      .filter(item => !item.adminOnly || isPracticeAdmin)
      .map(item => `
      <a class="nav-link" data-route="${item.hash}" href="#/${item.hash}">
        <span class="icon">${item.icon}</span>${item.label}
      </a>`).join("");
  } else if (isSchueler) {
    // Schüler-Portal-Navigation
    html += STUDENT_NAV
      .filter(isStudentNavVisible)
      .map(item => `
      <a class="nav-link" data-route="${item.hash}" href="#/${item.hash}">
        <span class="icon">${item.icon}</span>${item.label}
      </a>`).join("");
  } else if (inCarrierArea) {
    // Offene Anfragen zum Verbinden als Zähler an "Schulen", nur beim Haupt-Trägeradmin
    const openRequests = carrier?.isFounder ? carrier.openRequests ?? 0 : 0;
    html += CARRIER_NAV
      .filter(item => !item.founderOnly || carrier?.isFounder)
      .map(item => `
      <a class="nav-link" data-route="${item.hash}" href="#/${item.hash}">
        <span class="icon">${item.icon}</span>${item.label}${item.hash === "traeger-schulen" && openRequests > 0
          ? `<span class="nav-count" title="Offene Anfragen zum Verbinden">${openRequests}</span>` : ""}
      </a>`).join("");
  } else {
    // Schulnavigation nur wenn Schulzuordnung vorhanden
    if (hasSchool) {
      html += SCHOOL_NAV
        .filter(item => !item.adminOnly || isSchulAdmin)
        // Nicht gebuchte Module stehen nicht in der Leiste. Freischalten laesst
        // sich ueber die Modul-Kacheln im Dashboard und die Seite "Abonnements".
        .filter(item => !isNavLocked(item))
        .map(item => `
        <a class="nav-link" data-route="${item.hash}" href="#/${item.hash}">
          <span class="icon">${item.icon}</span>${item.label}
        </a>`).join("");
      // Benutzer als eigener Menuepunkt am Ende der Hauptnav (unter Planung),
      // nur fuer Schul-Admins. Einstellungen liegt im Footer unter Abonnements.
      if (isSchulAdmin) {
        html += `
        <a class="nav-link" data-route="benutzer" href="#/benutzer">
          <span class="icon">${Icons.userGroup}</span>Benutzer
        </a>`;
      }
    }

    // Admin-Link nur für Plattform-Admins
    if (isPlatformAdmin) {
      if (hasSchool) html += `<div style="border-top:1px solid var(--gray-200);margin:.5rem 0"></div>`;
      html += ADMIN_NAV.map(item => `
        <a class="nav-link" data-route="${item.hash}" href="#/${item.hash}">
          <span class="icon">${item.icon}</span>${item.label}
        </a>`).join("");
    }
  }

  nav.innerHTML = html;

  renderSidebarFooter();
}

/**
 * Abonnements und Einstellungen am Fuss der Leiste, nur fuer Schul-Admins.
 *
 * Steht man in den Einstellungen, klappen die Bereiche als Unterpunkte auf. Sie liegen
 * bewusst hier und nicht als zweite Navigationsspalte neben dem Inhalt: die Spalte hat
 * Breite gekostet, die den Tabellen fehlte. Die Unterpunkte aendern nur den Hash, den
 * Rest erledigt navigate().
 */
function renderSidebarFooter() {
  const settingsEl = $("sidebar-settings");
  if (isPraktikumsstelle) {
    settingsEl.innerHTML = `<a class="nav-link" data-route="praxis-einstellungen" href="#/praxis-einstellungen"><span class="icon">${Icons.cog6Tooth}</span>Einstellungen</a>`;
    return;
  }
  if (isSchueler || !hasSchool || inCarrierArea || !isSchulAdmin) {
    settingsEl.innerHTML = "";
    return;
  }

  const inSettings = getRoute() === "einstellungen";
  const activeTab = inSettings ? settingsHubPage.activeSectionId() : "";
  const subItems = inSettings
    ? settingsHubPage.listSections().map(s => `
      <a class="nav-sublink${s.id === activeTab ? " active" : ""}" href="#/einstellungen?tab=${s.id}">${s.label}</a>`).join("")
    : "";

  settingsEl.innerHTML = `
    <a class="nav-link" data-route="abonnements" href="#/abonnements"><span class="icon">${Icons.creditCard}</span>Abonnements</a>
    <a class="nav-link" data-route="einstellungen" href="#/einstellungen"><span class="icon">${Icons.cog6Tooth}</span>Einstellungen</a>
    ${subItems}`;
}

// Öffentliche Rechtsseiten (AGB, AVV) ohne Auth rendern
async function showPublicLegalPage(renderFn: (el: HTMLElement) => Promise<void>) {
  $("login-screen").classList.add("hidden");
  $("loading-screen").classList.add("hidden");
  $("app-shell").classList.remove("hidden");
  const sidebar = document.querySelector(".sidebar") as HTMLElement;
  if (sidebar) sidebar.style.display = "none";
  const main = $("main-content");
  main.style.marginLeft = "0";
  await renderFn(main);

  const onHashChange = () => {
    const h = window.location.hash;
    if (h !== "#/agb" && h !== "#/avv") {
      window.removeEventListener("hashchange", onHashChange);
      if (sidebar) sidebar.style.display = "";
      main.style.marginLeft = "";
      boot();
    }
  };
  window.addEventListener("hashchange", onHashChange);
}

// Registrierungsseite ohne Auth rendern
async function showRegisterPage(variant: "school" | "practice") {
  $("login-screen").classList.add("hidden");
  $("loading-screen").classList.add("hidden");
  $("app-shell").classList.remove("hidden");
  // Sidebar und Header verstecken, nur Content zeigen
  const sidebar = document.querySelector(".sidebar") as HTMLElement;
  if (sidebar) sidebar.style.display = "none";
  const main = $("main-content");
  main.style.marginLeft = "0";
  const renderFn = variant === "practice" ? registerPracticePage.render : registerPage.render;
  await renderFn(main);

  const expectedHash = variant === "practice" ? "#/registrieren/praktikumsstelle" : "#/registrieren/schule";
  const onHashChange = () => {
    if (window.location.hash !== expectedHash) {
      window.removeEventListener("hashchange", onHashChange);
      if (sidebar) sidebar.style.display = "";
      main.style.marginLeft = "";
      boot();
    }
  };
  window.addEventListener("hashchange", onHashChange);
}

// App starten
async function boot() {
  console.log("[Meduvio] boot() gestartet");
  initModal();

  // Registrierungsseite ohne Auth anzeigen
  if (window.location.hash === "#/registrieren" || window.location.hash === "#/registrieren/schule") {
    await showRegisterPage("school");
    return;
  }
  if (window.location.hash === "#/registrieren/praktikumsstelle") {
    await showRegisterPage("practice");
    return;
  }

  // Rechtliche Seiten ohne Auth anzeigen
  if (window.location.hash === "#/agb") {
    await showPublicLegalPage(agbPage.render);
    return;
  }
  if (window.location.hash === "#/avv") {
    await showPublicLegalPage(avvPage.render);
    return;
  }
  if (window.location.hash === "#/avv-praktikumsstelle") {
    await showPublicLegalPage(avvPracticePage.render);
    return;
  }

  $("login-screen").classList.add("hidden");
  $("loading-screen").classList.remove("hidden");

  try {
    const authenticated = await initKeycloak();
    console.log("[Meduvio] Keycloak authenticated:", authenticated);

    if (!authenticated) {
      $("loading-screen").classList.add("hidden");
      $("login-screen").classList.remove("hidden");
      $("btn-login").addEventListener("click", login);
      // Registrierungs-Link auf Login-Screen: Bei Hash-Änderung boot() erneut aufrufen
      const onHash = () => {
        window.removeEventListener("hashchange", onHash);
        boot();
      };
      window.addEventListener("hashchange", onHash);
      return;
    }

    // Token-Getter für API setzen
    setTokenGetter(getToken);

    // Praktikumsstellen-Branch: eigener Profil-Endpoint, kein api.me() (das setzt Schulzuordnung voraus)
    if (hasRole("praktikumsstelle")) {
      isPraktikumsstelle = true;
      isPracticeAdmin = hasRole("praktikumsstelle-admin");
      isPlatformAdmin = false;
      isSchulAdmin = false;
      isSchueler = false;
      hasSchool = false;
      userFeatures = [];

      // Profil best-effort laden; Fehler blockieren den Login nicht – die Seite "Meine Einrichtung" zeigt den Fehler + Retry.
      try {
        const org = await api.practiceOrg.me();
        $("sidebar-school").innerHTML = `
          <div class="label">Einrichtung</div>
          <div class="value" style="color:var(--primary)">${esc(org.name)}</div>
          <div class="sidebar-code">
            <div class="sidebar-code-label">Dein Code</div>
            <div class="sidebar-code-value" title="Code kopieren" id="sidebar-code-copy">${esc(org.code)}</div>
          </div>`;
        const copyEl = document.getElementById("sidebar-code-copy");
        copyEl?.addEventListener("click", async () => {
          try {
            await navigator.clipboard.writeText(org.code);
            copyEl.classList.add("copied");
            setTimeout(() => copyEl.classList.remove("copied"), 1200);
          } catch {}
        });
      } catch (err: any) {
        console.error("Praktikumsstellen-Profil konnte nicht geladen werden:", err);
        $("sidebar-school").innerHTML = `<div class="label">Einrichtung</div><div class="value" style="color:var(--primary)">${esc(getUsername())}</div>`;
      }

      buildSidebar();
      $("user-name").textContent = getUsername() || "";
      $("user-avatar").textContent = (getUsername() || "?").charAt(0).toUpperCase();

      // Logout-Button im User-Menü
      const userTrigger = $("user-menu-trigger");
      const userPanel = $("user-menu-panel");
      userTrigger.addEventListener("click", (e) => { e.stopPropagation(); userPanel.classList.toggle("open"); });
      document.addEventListener("click", () => userPanel.classList.remove("open"));
      $("btn-logout").addEventListener("click", logout);

      // Mobile Sidebar Toggle
      const btnMobileNav = document.getElementById("btn-mobile-nav");
      const mobileOverlay = document.getElementById("mobile-overlay");
      const sidebarEl = document.querySelector(".sidebar") as HTMLElement | null;
      if (btnMobileNav && mobileOverlay && sidebarEl) {
        btnMobileNav.addEventListener("click", () => {
          sidebarEl.classList.toggle("sidebar-open");
          mobileOverlay.classList.toggle("overlay-open");
        });
        mobileOverlay.addEventListener("click", () => {
          sidebarEl.classList.remove("sidebar-open");
          mobileOverlay.classList.remove("overlay-open");
        });
      }

      $("loading-screen").classList.add("hidden");
      $("app-shell").classList.remove("hidden");

      window.addEventListener("hashchange", navigate);
      const currentHash = window.location.hash.replace("#/", "").split("?")[0];
      const isValidStartRoute = PRACTICE_NAV.some(n => n.hash === currentHash);
      if (!isValidStartRoute) window.location.hash = "#/meine-einrichtung";
      await navigate();
      return;
    }

    // User-Info laden – bei Fehler Fehlermeldung mit Logout-Option zeigen (kein Login-Loop)
    let userInfo: Awaited<ReturnType<typeof api.me>>;
    try {
      userInfo = await api.me();
    } catch (err: any) {
      $("loading-screen").classList.add("hidden");
      $("login-screen").classList.remove("hidden");
      const btn = $("btn-login");
      btn.textContent = "Abmelden";
      btn.addEventListener("click", logout);
      const msg = document.createElement("p");
      msg.style.cssText = "color:#991b1b;font-size:.875rem;margin-top:1rem;text-align:center";
      msg.textContent = err.message || "Kein Schulzugang konfiguriert.";
      btn.parentElement?.appendChild(msg);
      return;
    }
    // Schulträger: Ein neuer Tab startet in der Standard-Schule, falls gesetzt. Für eine
    // fremde Schule wird /api/me danach mit X-Schule-Id erneut geladen.
    let startedInDefaultSchool = false;
    const defaultSchoolId = !userInfo.isSchueler ? userInfo.carrier?.defaultSchoolId : null;
    if (defaultSchoolId && !getCarrierView()) {
      startedInDefaultSchool = true;
      if (defaultSchoolId === Number(userInfo.schoolId)) {
        setCarrierView({ mode: "eigene" });
      } else {
        setCarrierView({ mode: "schule", schoolId: defaultSchoolId });
        try { userInfo = await api.me(); }
        catch { setCarrierView({ mode: "traeger" }); startedInDefaultSchool = false; }
      }
    }
    console.log("[Meduvio] User-Info:", JSON.stringify(userInfo));
    isPlatformAdmin = userInfo.isPlatformAdmin;
    isSchulAdmin = userInfo.isSchulAdmin;
    isSchueler = userInfo.isSchueler || false;
    hasSchool = !!userInfo.schoolId;
    userFeatures = userInfo.features || [];
    setPortalFeatures(userInfo.portalFeatures || []);
    // Betrachter sehen keine Schaltflächen zum Ändern von Daten
    setWriteAccess(userInfo.canWrite !== false);

    // Schulträger: Nach dem Login startet ein Träger-User im Träger-Bereich. Ohne Träger
    // (nie gehabt oder inzwischen entzogen) gilt eine gespeicherte Ansicht nicht mehr.
    carrier = isSchueler ? null : (userInfo.carrier ?? null);
    if (!carrier) {
      if (getCarrierView()) setCarrierView(null);
    } else if (!getCarrierView()) {
      setCarrierView({ mode: "traeger" });
    }
    inCarrierArea = !!carrier && getCarrierView()?.mode === "traeger";

    // Schüler-Kontext an students-Seite übergeben (für Login-Checkbox)
    studentsPage.setUserContext(userFeatures, isSchulAdmin);
    // Feature-Flags an Einstellungen-Hub (steuert optionale Bereiche, z.B. Stundenplan)
    settingsHubPage.setUserContext(userFeatures);
    // Klassen: Kursbeginn und Ausbildungsmodell nur mit gebuchtem Stundenplan
    classesPage.setUserContext(userFeatures);
    // Auswertungen: die Reiter haengen an Planung bzw. Stundenplan
    auswertungenPage.setUserContext(userFeatures);

    // UI aufbauen
    buildSidebar();

    // Sidebar-Schule/Schüler: Anzeige je nach Rolle
    if (isSchueler) {
      $("sidebar-school").innerHTML = `<div class="label">Schüler-Portal</div><div class="value" style="color:var(--primary)">${esc(userInfo.schoolName)}</div>`;
    } else if (inCarrierArea) {
      $("sidebar-school").innerHTML = `<div class="label">Schulträger</div><div class="value" style="color:#6d28d9">${esc(carrier!.name)}</div>`;
    } else if (hasSchool) {
      $("sidebar-school").innerHTML = `<div class="label">Schule</div><div class="value">${esc(userInfo.schoolName)}</div>`;
    } else if (isPlatformAdmin) {
      $("sidebar-school").innerHTML = `<div class="label">Rolle</div><div class="value" style="color:var(--primary)">Plattform-Admin</div>`;
    }

    $("user-name").textContent = getUsername() || "";
    $("user-avatar").textContent = (getUsername() || "?").charAt(0).toUpperCase();

    // User-Dropdown-Menü
    const userTrigger = $("user-menu-trigger");
    const userPanel = $("user-menu-panel");
    userTrigger.addEventListener("click", (e) => {
      e.stopPropagation();
      userPanel.classList.toggle("open");
    });
    document.addEventListener("click", () => userPanel.classList.remove("open"));
    $("btn-passwort").addEventListener("click", () => {
      userPanel.classList.remove("open");
      const eyeSvg = Icons.eye;
      const eyeOffSvg = Icons.eyeSlash;
      const pwField = (id: string, autocomplete: string) =>
        `<div class="pw-field"><input id="${id}" type="password" autocomplete="${autocomplete}" /><button type="button" class="pw-toggle" data-target="${id}" title="Passwort anzeigen">${eyeSvg}</button></div>`;
      openModal("Passwort ändern", `
        <div class="form-group"><label>Aktuelles Passwort *</label>${pwField("pw-current", "current-password")}</div>
        <div class="form-group"><label>Neues Passwort *</label>${pwField("pw-new", "new-password")}<ul id="pw-checklist" class="pw-checklist"></ul></div>
        <div class="form-group"><label>Bestätigung *</label>${pwField("pw-confirm", "new-password")}</div>
        <div id="pw-error" style="color:var(--danger,#dc2626);font-size:.875rem;min-height:1.2rem"></div>
        <div class="form-actions">
          <button class="btn btn-secondary" id="pw-cancel">Abbrechen</button>
          <button class="btn btn-primary" id="pw-save">Speichern</button>
        </div>
      `);
      const mb = modalBody();
      mountPasswordChecklist(mb.querySelector("#pw-new")!, mb.querySelector("#pw-checklist")!);
      mb.querySelectorAll<HTMLButtonElement>(".pw-toggle").forEach(btn => {
        btn.addEventListener("click", () => {
          const input = mb.querySelector<HTMLInputElement>(`#${btn.dataset.target}`)!;
          const show = input.type === "password";
          input.type = show ? "text" : "password";
          btn.innerHTML = show ? eyeOffSvg : eyeSvg;
          btn.title = show ? "Passwort verbergen" : "Passwort anzeigen";
        });
      });
      mb.querySelector("#pw-cancel")!.addEventListener("click", closeModal);
      mb.querySelector("#pw-save")!.addEventListener("click", async () => {
        const current = (mb.querySelector("#pw-current") as HTMLInputElement).value;
        const newPw   = (mb.querySelector("#pw-new") as HTMLInputElement).value;
        const confirm = (mb.querySelector("#pw-confirm") as HTMLInputElement).value;
        const errorEl = mb.querySelector("#pw-error") as HTMLElement;
        errorEl.textContent = "";
        if (!current || !newPw || !confirm) { errorEl.textContent = "Alle Felder sind Pflichtfelder"; return; }
        const pwErr = validatePassword(newPw);
        if (pwErr) { errorEl.textContent = pwErr; return; }
        if (newPw !== confirm) { errorEl.textContent = "Passwörter stimmen nicht überein"; return; }
        try {
          await api.changePassword(current, newPw);
          closeModal();
        } catch (err: any) {
          errorEl.textContent = err.message;
        }
      });
    });
    $("btn-logout").addEventListener("click", logout);

    // Schulträger: Wechsel zwischen Träger-Bereich und eigener Schule im Benutzer-Menü,
    // Hinweisbalken in einer fremden Schule. Benutzer ohne Träger sehen beides nicht.
    if (carrier) {
      const btnTraeger = $("btn-traeger");
      btnTraeger.innerHTML = inCarrierArea
        ? `${Icons.home} Zur eigenen Schule`
        : `${Icons.buildingLibrary} Zum Träger-Bereich`;
      btnTraeger.classList.remove("hidden");
      btnTraeger.addEventListener("click", () => {
        userPanel.classList.remove("open");
        if (inCarrierArea) switchCarrierView({ mode: "eigene" }, "#/dashboard");
        else switchCarrierView({ mode: "traeger" }, "#/traeger");
      });
      if (userInfo.carrierAccess) {
        const banner = $("carrier-banner");
        banner.innerHTML = `<span>Du arbeitest in <strong>${esc(userInfo.schoolName)}</strong> über den Schulträger ${esc(carrier.name)}.</span><a href="#/traeger" id="carrier-banner-back">Zum Träger-Bereich</a>`;
        banner.classList.remove("hidden");
        document.body.classList.add("has-carrier-banner");
      }
    }

    // Mobile Sidebar Toggle
    const btnMobileNav = document.getElementById("btn-mobile-nav");
    const mobileOverlay = document.getElementById("mobile-overlay");
    const sidebarEl = document.querySelector(".sidebar") as HTMLElement | null;
    if (btnMobileNav && mobileOverlay && sidebarEl) {
      btnMobileNav.addEventListener("click", () => {
        sidebarEl.classList.toggle("sidebar-open");
        mobileOverlay.classList.toggle("overlay-open");
      });
      mobileOverlay.addEventListener("click", () => {
        sidebarEl.classList.remove("sidebar-open");
        mobileOverlay.classList.remove("overlay-open");
      });
    }

    // Screens umschalten
    $("loading-screen").classList.add("hidden");
    $("app-shell").classList.remove("hidden");

    // Router starten
    window.addEventListener("hashchange", navigate);
    let defaultRoute: string;
    if (isSchueler) {
      defaultRoute = "#/mein-dashboard";
    } else if (inCarrierArea) {
      defaultRoute = "#/traeger";
    } else {
      defaultRoute = isPlatformAdmin && !hasSchool ? "#/admin" : "#/dashboard";
    }
    // In der Standard-Schule nicht über einen alten Träger-Link gleich wieder hinaus
    if (startedInDefaultSchool && window.location.hash.replace("#/", "").startsWith("traeger")) {
      history.replaceState(null, "", "#/dashboard");
    }
    const currentHash = window.location.hash.replace("#/", "").split("?")[0];
    const isValidStartRoute = isSchueler
      ? STUDENT_NAV.filter(isStudentNavVisible).some(n => n.hash === currentHash)
      : inCarrierArea
        ? CARRIER_NAV.some(n => n.hash === currentHash && (!n.founderOnly || carrier?.isFounder))
        : !!currentHash;
    if (!isValidStartRoute) window.location.hash = defaultRoute;
    await navigate();

  } catch (err: any) {
    console.error("Boot-Fehler:", err);
    $("loading-screen").classList.add("hidden");
    $("login-screen").classList.remove("hidden");
    $("btn-login").addEventListener("click", login);
    const onHash = () => {
      window.removeEventListener("hashchange", onHash);
      boot();
    };
    window.addEventListener("hashchange", onHash);
  }
}

// Start
document.addEventListener("DOMContentLoaded", boot);
